import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import emailjs from '@emailjs/browser'; // Import EmailJS

const AIEducationLanding = () => {
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institutionName: '',
    institutionType: '',
    role: '',
    questions: ''
  });

  // Modal handlers
  const handleShowModal = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      institutionName: '',
      institutionType: '',
      role: '',
      questions: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Updated handleSubmit function
  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Dynamically build the email body as a plain string
    const emailBody = `
New AI Training Session Booking:

Contact Details:
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone || 'Not provided'}

Institution Details:
- Institution: ${formData.institutionName}
- Type: ${formData.institutionType}
- Role: ${formData.role}

Questions or Requirements:
${formData.questions || 'None'}
    `;

    // 2. Prepare the parameters for the universal template
    const templateParams = {
      email_subject: 'Book an AI Training Session', // Specific subject for this form
      email_body: emailBody.trim()
    };

    // 3. Use your EmailJS credentials
    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for booking! We will contact you soon to schedule your AI training session.');
        handleCloseModal();
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('Failed to send the request. Please try again later.');
      });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0a0a0a',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '0'
    },
    wrapper: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '48px 24px'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '24px',
      padding: '8px 16px',
      marginBottom: '32px',
      backdropFilter: 'blur(8px)'
    },
    badgeIcon: {
      width: '20px',
      height: '20px',
      background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10px'
    },
    badgeText: {
      fontSize: '14px',
      fontWeight: '500',
      background: 'linear-gradient(45deg, #60a5fa, #a78bfa)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    badgeLabel: {
      fontSize: '12px',
      color: '#9ca3af'
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      alignItems: 'center'
    },
    leftSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px'
    },
    title: {
      fontSize: '3.5rem',
      fontWeight: '700',
      lineHeight: '1.1',
      marginBottom: '24px'
    },
    titleGradient: {
      background: 'linear-gradient(45deg, #60a5fa, #a78bfa)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#d1d5db',
      lineHeight: '1.6',
      marginBottom: '32px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '16px'
    },
    primaryButton: {
      background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
      color: 'white',
      border: 'none',
      padding: '16px 32px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
    },
    secondaryButton: {
      background: 'transparent',
      color: '#d1d5db',
      border: '1px solid #374151',
      padding: '16px 32px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    featureCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '24px',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease'
    },
    featureIcon: {
      width: '48px',
      height: '48px',
      background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px'
    },
    featureTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '8px'
    },
    featureDescription: {
      color: '#9ca3af',
      fontSize: '0.95rem',
      lineHeight: '1.5'
    },
    responsive: {
      '@media (max-width: 1024px)': {
        gridTemplateColumns: '1fr'
      }
    }
  };

  // Hover effects
  const handlePrimaryButtonHover = (e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
  };

  const handlePrimaryButtonLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.3)';
  };

  const handleSecondaryButtonHover = (e) => {
    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
    e.target.style.borderColor = '#60a5fa';
  };

  const handleSecondaryButtonLeave = (e) => {
    e.target.style.background = 'transparent';
    e.target.style.borderColor = '#374151';
  };

  const handleCardHover = (e) => {
    e.target.style.transform = 'translateY(-4px)';
    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
    e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
  };

  const handleCardLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.wrapper}>
          <div style={window.innerWidth > 1024 ? styles.mainGrid : {display: 'flex', flexDirection: 'column', gap: '48px'}}>
            {/* Left Section */}
            <div style={styles.leftSection}>
              <div>
                <h1 style={styles.title}>
                  Future-Ready{' '}
                  <span style={styles.titleGradient}>AI Education</span>
                </h1>
                <p style={styles.subtitle}>
                  Our AI Trainer Kit grows with your educational needs, from basic learning to advanced research capabilities.
                </p>
              </div>

              <div style={styles.buttonGroup}>
                <button 
                  style={styles.primaryButton}
                  onMouseEnter={handlePrimaryButtonHover}
                  onMouseLeave={handlePrimaryButtonLeave}
                  onClick={handleShowModal}
                >
                  Book an AI Training Session
                </button>
              </div>
            </div>

            {/* Right Section */}
            <div style={styles.rightSection}>
              <div 
                style={styles.featureCard}
                onMouseEnter={handleCardHover}
                onMouseLeave={handleCardLeave}
              >
                <div style={styles.featureIcon}>🎯</div>
                <h3 style={styles.featureTitle}>Sensor & IoT Integration</h3>
                <p style={styles.featureDescription}>
                  Easily add sensors and IoT kits to support advanced AI and automation experiments.
                </p>
              </div>

              <div 
                style={styles.featureCard}
                onMouseEnter={handleCardHover}
                onMouseLeave={handleCardLeave}
              >
                <div style={styles.featureIcon}>💡</div>
                <h3 style={styles.featureTitle}>Custom Design Support</h3>
                <p style={styles.featureDescription}>
                  Lab setup and tools can be tailored for faculty research, student innovation, or specific departmental needs.
                </p>
              </div>

              <div 
                style={styles.featureCard}
                onMouseEnter={handleCardHover}
                onMouseLeave={handleCardLeave}
              >
                <div style={styles.featureIcon}>📚</div>
                <h3 style={styles.featureTitle}>Modular Expansion</h3>
                <p style={styles.featureDescription}>
                  Ready to incorporate future technologies like autonomous systems & LLM.
                </p>
              </div>

              <div 
                style={styles.featureCard}
                onMouseEnter={handleCardHover}
                onMouseLeave={handleCardLeave}
              >
                <div style={styles.featureIcon}>🚀</div>
                <h3 style={styles.featureTitle}>Upgrade-Friendly</h3>
                <p style={styles.featureDescription}>
                  Scales from basic learning environments to advanced research setups with minimal effort.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal for AI Training Session Booking */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton style={{ 
          borderBottom: '1px solid #e2e8f0', 
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🎯 Book Your AI Training Session
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem', background: '#ffffff' }}>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Full Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Email Address *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Phone Number *
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Institution Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="institutionName"
                    value={formData.institutionName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your institution name"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Institution Type *
                  </Form.Label>
                  <Form.Select
                    name="institutionType"
                    value={formData.institutionType}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="">Select Type</option>
                    <option value="university">University</option>
                    <option value="college">College</option>
                    <option value="school">School</option>
                    <option value="training-center">Training Center</option>
                    <option value="corporate">Corporate</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Your Role *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Professor, Administrator, IT Director, Training Manager"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                Questions or Training Requirements
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="questions"
                value={formData.questions}
                onChange={handleInputChange}
                placeholder="Tell us about your specific training needs, goals, number of participants, preferred timing, or any questions about our AI training programs..."
                style={{ 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease',
                  resize: 'vertical'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </Form.Group>

            <div style={{
              background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #3b82f6'
            }}>
              <p style={{ 
                margin: 0, 
                fontSize: '0.8rem', 
                color: '#1e40af',
                lineHeight: '1.4'
              }}>
                🚀 <strong>What's Included:</strong> Custom curriculum design, hands-on training materials, expert instructors, and ongoing support. We'll schedule a consultation call within 24 hours to discuss your specific requirements.
              </p>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={handleCloseModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: '2px solid #e2e8f0',
                  color: '#6b7280'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                }}
              >
                Book Training Session
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AIEducationLanding;